import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  useTheme,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { Footer } from '../components/Footer';
import { animations, transitions } from '../theme/animations';

export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const theme = useTheme();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const from = (location.state as { from?: { pathname?: string } })?.from?.pathname || '/';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login({ email, password });

      // Navigate to the page they were trying to access, or home
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Record<string, string> }; status?: number } };
      const errorMessage = error.response?.data?.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
      <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            py: 4,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              p: 4,
              width: '100%',
              borderRadius: 3,
              animation: animations.scaleIn,
              transition: transitions.default,
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(29, 56, 141, 0.1) 0%, rgba(0, 0, 0, 0.3) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(29, 56, 141, 0.05) 100%)',
              '&:hover': {
                boxShadow: 12,
              },
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 2,
              }}>
                <Box
                  component="img"
                  src={theme.palette.mode === 'dark'
                    ? '/assets/om-client-manager-vertical-white.png'
                    : '/assets/om-client-manager-vertical.png'}
                  alt="Waldevar O&M Client Manager"
                  sx={{ width: '100%', maxWidth: 440, height: 'auto' }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Renewable Energy Asset Management
              </Typography>
            </Box>

            {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoFocus
              autoComplete="email"
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{
                mt: 3,
                mb: 2,
                background: 'linear-gradient(90deg, #1d388d 0%, #4a90e2 100%)',
                transition: transitions.default,
                '&:hover': {
                  background: 'linear-gradient(90deg, #4a90e2 0%, #1d388d 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: 6,
                },
              }}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </Paper>
        <Footer variant="inline" />
      </Box>
    </Container>
  );
};

